/* =====================================================================
   Pink Ribbon Catcher
   Reusable "catch the ribbon" interaction built on lottie-web.
   - One small clickable <button> per ribbon; no full-screen overlay.
   - Picks random positions that avoid excluded DOM areas.
   - Cleans up the DOM node + Lottie instance after every appearance.
   Requires: window.lottie (lottie-web) loaded before start().
   ===================================================================== */
(function (global) {
  'use strict';

  var DEFAULTS = {
    // Lottie source: either a URL (animationPath) or a parsed object (animationData)
    animationPath: 'pink-ribbon.json',
    animationData: null,

    // Optional recolor of every static fill/stroke, e.g. '#E4007C'. null = keep file colors.
    recolor: null,

    initialDelay: 3000,          // ms before the first ribbon
    delayBetween: [4000, 8000],  // ms [min, max] between two appearances
    visibleDuration: 4000,       // ms the ribbon stays catchable
    maxAppearances: 5,           // total appearances per page load (Infinity allowed)
    maxCatches: 1,               // stop the game after this many catches
    retryDelay: 1500,            // ms to wait when no safe position was found

    perWave: 1,                  // ribbons per appearance: a number, or [min, max] for random
    minDistance: 180,            // px min gap between ribbon centers (same wave and vs. previous wave)
    size: 110,                   // px width (height follows the JSON aspect ratio)
    edgeMargin: 24,              // px min distance from viewport edges
    zIndex: 9990,
    anchor: 'page',              // 'page' = scrolls with content, 'viewport' = position: fixed

    // Mobile overrides (applied under the breakpoint)
    breakpoint: 767,
    desktop: { enabled: true },
    mobile: { enabled: true, size: 80, edgeMargin: 16 },

    // Areas the ribbon must never cover, whatever their size
    strictExcludeSelectors: ['header', 'nav', '[role="dialog"]', '[data-ribbon-exclude]'],
    // Interactive elements to avoid. Ones bigger than maxExcludeArea (fraction of the
    // viewport, e.g. a full-width banner link) are ignored, otherwise nothing is left.
    excludeSelectors: [
      'form', 'input', 'select', 'textarea', 'button:not(.pr-ribbon)', 'a[href]', '[role="button"]'
    ],
    maxExcludeArea: 0.3,
    excludePadding: 12,          // px added around every excluded element

    // Return false to postpone an appearance (e.g. while another popup is open)
    canSpawn: null,
    positionAttempts: 40,

    // 'static' = still ribbon without motion, 'disable' = no interaction at all
    reducedMotion: 'static',
    forceReducedMotion: false,   // testing only

    // CSS pop-in on top of the Lottie playback (safety net when the file has no entrance)
    cssEntrance: true,
    debug: false,                // outlines hit area + excluded zones
    decorative: false,           // true = animation only: not clickable, hidden from screen readers

    onSpawn: null,               // function (info)
    onCatch: null,               // function (info)  <- connect the promo popup here
    onMiss: null,                // function (info)
    onComplete: null             // function (state) when the game ends
  };

  var textCache = {};

  function extend(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i] || {};
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  function loadText(path) {
    if (!textCache[path]) {
      textCache[path] = fetch(path, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('Ribbon JSON failed to load: ' + r.status);
        return r.text();
      });
    }
    return textCache[path];
  }

  function hexToLottie(hex) {
    var h = hex.replace('#', '');
    if (h.length === 3) h = h.replace(/./g, '$&$&');
    return [0, 2, 4].map(function (i) { return parseInt(h.substr(i, 2), 16) / 255; }).concat(1);
  }

  // Replace static and keyframed colors of fills ('fl') and strokes ('st')
  function recolorData(data, hex) {
    if (typeof hex !== 'string' || !/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex.trim())) return data; // keep file colors
    var rgba = hexToLottie(hex.trim());
    (function walk(node) {
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (!node || typeof node !== 'object') return;
      if ((node.ty === 'fl' || node.ty === 'st') && node.c) {
        if (node.c.a === 0) node.c.k = rgba.slice();
        else if (Array.isArray(node.c.k)) node.c.k.forEach(function (kf) { if (kf.s) kf.s = rgba.slice(); });
      }
      // Gradients: keep the light/dark shading, map it onto light pink -> deep pink
      if ((node.ty === 'gf' || node.ty === 'gs') && node.g && node.g.k && node.g.k.a === 0) {
        var stops = node.g.k.k, count = node.g.p;
        var light = rgba.slice(0, 3).map(function (c) { return c + (1 - c) * 0.45; });
        var dark = rgba.slice(0, 3).map(function (c) { return c * 0.75; });
        for (var i = 0; i < count; i++) {
          var o = i * 4 + 1;
          var lum = (stops[o] + stops[o + 1] + stops[o + 2]) / 3;
          for (var c = 0; c < 3; c++) stops[o + c] = dark[c] + (light[c] - dark[c]) * lum;
        }
      }
      for (var k in node) walk(node[k]);
    })(data);
    return data;
  }

  function PinkRibbonCatcher(options) {
    this.opts = extend({}, DEFAULTS, options);
    this.state = { appearances: 0, catches: 0, misses: 0, running: false };
    this._timer = null;
    this._wave = null;         // ribbons currently on screen { recs: [], timer }
    this._lastCenters = [];    // page coords of the previous wave, so the next one lands elsewhere
    this._source = null;       // JSON string, cloned for every instance (lottie mutates data)
    this._onVisibility = this._onVisibility.bind(this);
  }

  PinkRibbonCatcher.prototype = {
    start: function () {
      if (this.state.running) return this;
      if (this._reducedMotion() && this.opts.reducedMotion === 'disable') return this;
      if (!this._deviceEnabled()) return this;
      this.state.running = true;
      document.addEventListener('visibilitychange', this._onVisibility);
      this._schedule(this.opts.initialDelay);
      return this;
    },

    stop: function () {
      this.state.running = false;
      clearTimeout(this._timer);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._clearWave();
      return this;
    },

    destroy: function () {
      this.stop();
      this._clearDebugZones();
    },

    // Remove ribbons on screen (e.g. another popup just opened) and try again later.
    // The appearance isn't retried; canSpawn keeps postponing until it's clear.
    interrupt: function () {
      if (!this.state.running || !this._wave) return;
      this._clearWave();
      this._schedule(this.opts.retryDelay);
    },

    // Spawn immediately (testing)
    spawnNow: function () {
      clearTimeout(this._timer);
      this._clearWave();
      this.state.running = true;
      this._spawn();
    },

    _schedule: function (delay) {
      var self = this;
      clearTimeout(this._timer);
      this._timer = setTimeout(function () { self._spawn(); }, delay);
    },

    _nextDelay: function () {
      var d = this.opts.delayBetween;
      return Array.isArray(d) ? rand(d[0], d[1]) : d;
    },

    _onVisibility: function () {
      // Don't spend appearances while the tab is hidden
      if (document.hidden) { clearTimeout(this._timer); }
      else if (this.state.running && !this._wave) { this._schedule(this.opts.retryDelay); }
    },

    _isMobile: function () { return window.matchMedia('(max-width: ' + this.opts.breakpoint + 'px)').matches; },
    _device: function () { return extend({}, this.opts, this._isMobile() ? this.opts.mobile : this.opts.desktop); },
    _deviceEnabled: function () { return this._device().enabled !== false; },
    _reducedMotion: function () {
      return this.opts.forceReducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    _getSource: function () {
      var self = this;
      if (this._source) return Promise.resolve(this._source);
      var p = this.opts.animationData
        ? Promise.resolve(JSON.stringify(this.opts.animationData))
        : loadText(this.opts.animationPath);
      return p.then(function (text) {
        if (self.opts.recolor) text = JSON.stringify(recolorData(JSON.parse(text), self.opts.recolor));
        self._source = text;
        return text;
      });
    },

    _finished: function () {
      var o = this.opts, s = this.state;
      return s.appearances >= o.maxAppearances || s.catches >= o.maxCatches;
    },

    _excludedRects: function (pad) {
      var rects = [];
      var vw = window.innerWidth, vh = window.innerHeight;
      var maxArea = this.opts.maxExcludeArea * vw * vh;
      var collect = function (selectors, capped) {
        if (!selectors || !selectors.length) return;
        var nodes = document.querySelectorAll(selectors.join(','));
        for (var i = 0; i < nodes.length; i++) {
          var r = nodes[i].getBoundingClientRect();
          if (!r.width || !r.height) continue;                           // hidden
          if (r.bottom < 0 || r.right < 0 || r.top > vh || r.left > vw) continue; // off-screen
          if (capped && r.width * r.height > maxArea) continue;          // huge link/banner
          rects.push({ left: r.left - pad, top: r.top - pad, right: r.right + pad, bottom: r.bottom + pad });
        }
      };
      collect(this.opts.strictExcludeSelectors, false);
      collect(this.opts.excludeSelectors, true);
      return rects;
    },

    // Up to `count` positions (viewport coords) that avoid excluded areas and each other.
    // With 2+ ribbons the screen is split into columns and each ribbon gets its own side.
    _findPositions: function (count, w, h, margin, minDistance) {
      var vw = document.documentElement.clientWidth, vh = window.innerHeight;
      if (vw - w - margin < margin || vh - h - margin < margin) return [];
      var rects = this._excludedRects(this.opts.excludePadding);
      if (this.opts.debug) this._drawDebugZones(rects);

      var sx = window.scrollX, sy = window.scrollY;
      var taken = this._lastCenters.slice();       // page coords
      var colW = vw / count;
      var cols = [];
      for (var c = 0; c < count; c++) cols.push(c);
      cols.sort(function () { return Math.random() - 0.5; });

      var found = [];
      for (var i = 0; i < cols.length; i++) {
        // Column bounds (a single ribbon uses the whole width)
        var gap = count > 1 ? margin / 2 : 0;
        var minX = Math.max(margin, cols[i] * colW + gap);
        var maxX = Math.min(vw - w - margin, (cols[i] + 1) * colW - w - gap);
        if (maxX < minX) maxX = minX;
        for (var n = 0; n < this.opts.positionAttempts; n++) {
          var x = rand(minX, maxX), y = rand(margin, vh - h - margin);
          var hit = rects.some(function (r) {
            return x < r.right && x + w > r.left && y < r.bottom && y + h > r.top;
          });
          if (hit) continue;
          var cx = x + w / 2 + sx, cy = y + h / 2 + sy;
          var close = taken.some(function (t) {
            return Math.sqrt((t.x - cx) * (t.x - cx) + (t.y - cy) * (t.y - cy)) < minDistance;
          });
          if (close) continue;
          taken.push({ x: cx, y: cy });
          found.push({ x: x, y: y, cx: cx, cy: cy });
          break;
        }
      }
      return found;
    },

    _spawn: function () {
      var self = this;
      if (!this.state.running || this._finished()) return this._complete();
      if (document.hidden || this._wave) return;
      if (typeof this.opts.canSpawn === 'function' && this.opts.canSpawn() === false) {
        return this._schedule(this.opts.retryDelay);   // postponed, not counted
      }

      this._getSource().then(function (text) {
        if (!self.state.running || self._wave) return;
        var dev = self._device();
        var ratio = (function (d) { return d.h / d.w; })(JSON.parse(text));
        var w = dev.size, h = Math.round(dev.size * ratio);
        // perWave: a number, or [min, max] for a random count each appearance
        var pw = dev.perWave || 1;
        var count = Array.isArray(pw) ? pw[0] + Math.floor(Math.random() * (pw[1] - pw[0] + 1)) : Math.round(pw);
        count = Math.max(1, count);
        var spots = self._findPositions(count, w, h, dev.edgeMargin, dev.minDistance);
        if (!spots.length) return self._schedule(self.opts.retryDelay); // no safe spot right now; not counted

        self.state.appearances++;
        self._lastCenters = spots.map(function (p) { return { x: p.cx, y: p.cy }; });
        var wave = { recs: [], timer: null, number: self.state.appearances };
        self._wave = wave;
        spots.forEach(function (pos) { wave.recs.push(self._render(JSON.parse(text), pos, w, h, wave)); });

        wave.timer = setTimeout(function () {
          wave.recs.forEach(function (rec) {
            if (rec.done) return;
            self.state.misses++;
            self._remove(rec, 'is-missed');
            if (typeof self.opts.onMiss === 'function') self.opts.onMiss(self._info(rec, wave));
          });
          self._endWave(wave);
        }, dev.visibleDuration);
      }).catch(function (err) {
        console.error('[PinkRibbonCatcher]', err);
        self.stop();
      });
    },

    _info: function (rec, wave) {
      return { appearance: wave.number, ribbons: wave.recs.length, catches: this.state.catches, element: rec.el, position: rec.pos };
    },

    _render: function (data, pos, w, h, wave) {
      var self = this, o = this.opts;
      var reduced = this._reducedMotion();
      var fixed = o.anchor === 'viewport';

      var el = document.createElement(o.decorative ? 'div' : 'button');
      if (!o.decorative) el.type = 'button';
      el.className = 'pr-ribbon' + (reduced ? ' is-reduced' : '') + (o.cssEntrance ? ' has-css-entrance' : '') + (o.debug ? ' is-debug' : '');
      if (o.decorative) { el.setAttribute('aria-hidden', 'true'); el.classList.add('pr-ribbon--decor'); }
      else el.setAttribute('aria-label', 'Catch the pink ribbon');
      el.style.cssText =
        'position:' + (fixed ? 'fixed' : 'absolute') + ';' +
        'left:' + Math.round(pos.x + (fixed ? 0 : window.scrollX)) + 'px;' +
        'top:' + Math.round(pos.y + (fixed ? 0 : window.scrollY)) + 'px;' +
        'width:' + w + 'px;height:' + h + 'px;z-index:' + o.zIndex + ';';

      var art = document.createElement('span');
      art.className = 'pr-ribbon__art';
      el.appendChild(art);
      document.body.appendChild(el);

      var anim = global.lottie.loadAnimation({
        container: art,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData: data,
        rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
      });

      var rec = { el: el, anim: anim, pos: pos, done: false, onClick: null };

      anim.addEventListener('DOMLoaded', function () {
        if (rec.done) return;
        if (reduced) anim.goToAndStop(anim.totalFrames - 1, true); // final pose, no motion
        else anim.play();
        requestAnimationFrame(function () { if (!rec.done) el.classList.add('is-visible'); });
      });

      rec.onClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (rec.done || wave.ended) return;   // duplicate click guard
        el.disabled = true;
        self.state.catches++;
        var detail = self._info(rec, wave);
        self._remove(rec, 'is-caught');
        // The other ribbons of this wave leave quietly
        wave.recs.forEach(function (other) { if (!other.done) self._remove(other, 'is-missed'); });
        document.dispatchEvent(new CustomEvent('pinkribbon:caught', { detail: detail }));
        if (typeof o.onCatch === 'function') o.onCatch(detail);
        self._endWave(wave);
      };
      if (!o.decorative) el.addEventListener('click', rec.onClick);

      if (typeof o.onSpawn === 'function') o.onSpawn(self._info(rec, wave));
      return rec;
    },

    _endWave: function (wave) {
      if (wave.ended) return;
      wave.ended = true;
      clearTimeout(wave.timer);
      if (this._wave === wave) this._wave = null;
      if (!this.state.running) return;
      if (this._finished()) this._complete();
      else this._schedule(this._nextDelay());
    },

    _clearWave: function () {
      var wave = this._wave;
      if (!wave) return;
      var self = this;
      wave.recs.forEach(function (rec) { self._remove(rec, 'is-missed'); });
      wave.ended = true;
      clearTimeout(wave.timer);
      this._wave = null;
    },

    // Exit transition, then destroy Lottie + remove node
    _remove: function (rec, exitClass) {
      if (rec.removed) return;
      rec.removed = true;
      rec.done = true;
      rec.el.removeEventListener('click', rec.onClick);
      rec.el.classList.remove('is-visible');
      rec.el.classList.add(exitClass);

      var cleaned = false;
      var cleanup = function () {
        if (cleaned) return;
        cleaned = true;
        rec.anim.destroy();
        if (rec.el.parentNode) rec.el.parentNode.removeChild(rec.el);
        rec.el = rec.anim = null;
      };
      rec.el.addEventListener('transitionend', function (e) { if (e.propertyName === 'opacity') cleanup(); });
      setTimeout(cleanup, 700); // fallback if no transition fires
    },

    _complete: function () {
      if (!this.state.running) return;
      this.state.running = false;
      clearTimeout(this._timer);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (typeof this.opts.onComplete === 'function') this.opts.onComplete(extend({}, this.state));
    },

    _drawDebugZones: function (rects) {
      this._clearDebugZones();
      var frag = document.createDocumentFragment();
      rects.forEach(function (r) {
        var z = document.createElement('div');
        z.className = 'pr-debug-zone';
        z.style.cssText = 'left:' + r.left + 'px;top:' + r.top + 'px;width:' + (r.right - r.left) + 'px;height:' + (r.bottom - r.top) + 'px;';
        frag.appendChild(z);
      });
      document.body.appendChild(frag);
      var self = this;
      clearTimeout(this._debugTimer);
      this._debugTimer = setTimeout(function () { self._clearDebugZones(); }, 1500);
    },

    _clearDebugZones: function () {
      var zones = document.querySelectorAll('.pr-debug-zone');
      for (var i = 0; i < zones.length; i++) zones[i].parentNode.removeChild(zones[i]);
    }
  };

  PinkRibbonCatcher.recolorData = recolorData;
  global.PinkRibbonCatcher = PinkRibbonCatcher;
})(window);
